import { useState } from 'react';
import { Button } from '../ui/button';
import { Sparkles, Check, X, RefreshCw, ThumbsUp, ThumbsDown } from 'lucide-react';
import { toast } from 'sonner';

/**
 * "✨ Suggest with AI" button + inline suggestion renderer for the
 * Industry Architect Seller Context form. May 19, 2026.
 *
 * Wraps:
 *   - The gold button itself (gated by `requireFields`)
 *   - Loading state while Haiku generates
 *   - Inline rendering of chips / cards (one each is Add or Dismiss)
 *   - Error / rate-limit / preflight messages
 *
 * Props:
 *   section          — backend section id (value_proposition | value_drivers
 *                      | differentiators | problems | buyer_roles)
 *   profile          — the full current Seller Context profile (passed to AI)
 *   requireFields    — list of profile keys that must be non-empty
 *                      e.g. ['seller_industry', 'target_segments', 'company_name']
 *                      If any required field is empty, the button is disabled
 *                      with a tooltip.
 *   onAcceptChip     — (text) => void; called when user clicks ✓ on a chip
 *   onAcceptRole     — (role) => void; called for buyer_roles section only
 *   onReplaceText    — (text) => void; called for value_proposition section only
 *   api              — useApi() hook instance
 *   testIdPrefix     — string prefix for data-testid (e.g. "value-drivers")
 */
export function SectionSuggestButton({
  section,
  profile,
  requireFields = ['seller_industry', 'target_segments'],
  onAcceptChip,
  onAcceptRole,
  onReplaceText,
  api,
  testIdPrefix,
  label = '✨ Suggest with AI',
}) {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [chips, setChips] = useState([]); // array of strings
  const [roles, setRoles] = useState([]); // array of buyer-role objects
  const [draft, setDraft] = useState(null); // string (for value_proposition)
  const [remaining, setRemaining] = useState(null);
  // Silent thumbs feedback per suggestion text. May 19, 2026.
  //   { [suggestionText]: 'up' | 'down' }
  const [votes, setVotes] = useState({});

  async function castVote(text, nextVote) {
    if (!text) return;
    const prev = votes[text];
    // Toggle off if user re-clicks the same direction
    const toSend = prev === nextVote ? 'clear' : nextVote;
    // Optimistic local update first — silent UI must feel instant
    setVotes((v) => {
      const out = { ...v };
      if (toSend === 'clear') delete out[text];
      else out[text] = toSend;
      return out;
    });
    try {
      await api.post('/sales-intelligence/industry-architect/suggest-feedback', {
        section,
        suggestion_text: text,
        vote: toSend,
        context: {
          seller_industry: profile?.seller_industry,
          target_segments: profile?.target_segments,
          company_name: profile?.company_name,
        },
      });
    } catch {
      // Silent feature — do NOT show a toast or revert state on transient failure.
      // The next vote/page-load will overwrite or no-op.
    }
  }

  const missing = requireFields.filter((f) => {
    const v = profile?.[f];
    if (Array.isArray(v)) return v.length === 0;
    return !v || (typeof v === 'string' && !v.trim());
  });
  const gated = missing.length > 0;

  const tooltip = gated
    ? section === 'value_proposition'
      ? 'Add your company name, industry, and target segments first for better suggestions'
      : 'Add your industry and target segments first for better suggestions'
    : '';

  async function runSuggest() {
    setLoading(true);
    setError(null);
    setChips([]);
    setRoles([]);
    setDraft(null);
    try {
      const res = await api.post('/sales-intelligence/industry-architect/suggest', {
        section,
        profile,
      });
      if (res?.remaining_today != null) setRemaining(res.remaining_today);
      if (!res?.ok) {
        setError(res?.error || 'We couldn\'t generate suggestions just now.');
        return;
      }
      if (section === 'value_proposition') {
        setDraft(res.value_proposition);
      } else if (section === 'buyer_roles') {
        setRoles(res.buyer_roles || []);
      } else {
        setChips(res[section] || []);
      }
    } catch (e) {
      setError(e?.message || 'Network error');
    } finally {
      setLoading(false);
    }
  }

  function dismissChipAt(idx) {
    setChips((prev) => prev.filter((_, i) => i !== idx));
  }
  function acceptChipAt(idx) {
    const text = chips[idx];
    if (text && onAcceptChip) {
      onAcceptChip(text);
      toast.success('Added — remember to save');
    }
    dismissChipAt(idx);
  }

  function dismissRoleAt(idx) {
    setRoles((prev) => prev.filter((_, i) => i !== idx));
  }
  function acceptRoleAt(idx) {
    const role = roles[idx];
    if (role && onAcceptRole) {
      onAcceptRole(role);
      toast.success(`Added ${role.title} — remember to save`);
    }
    dismissRoleAt(idx);
  }

  function acceptDraft() {
    if (draft && onReplaceText) {
      onReplaceText(draft);
      toast.success('Draft inserted — edit to make it yours');
    }
    setDraft(null);
  }

  return (
    <div className="space-y-2" data-testid={`${testIdPrefix}-suggest-wrapper`}>
      <div className="flex items-center gap-2">
        <Button
          type="button"
          variant="outline"
          size="sm"
          onClick={runSuggest}
          disabled={loading || gated}
          title={tooltip}
          className="border-[#B89B5E] text-[#8A6F2E] hover:bg-[#FAF4E6] hover:text-[#8A6F2E]"
          data-testid={`${testIdPrefix}-suggest-btn`}
        >
          {loading ? (
            <>
              <RefreshCw className="h-3.5 w-3.5 mr-1.5 animate-spin" />
              Generating suggestions...
            </>
          ) : (
            <>
              <Sparkles className="h-3.5 w-3.5 mr-1.5 text-[#B89B5E]" />
              {label}
            </>
          )}
        </Button>
        {remaining != null && remaining < 3 && (
          <span className="text-xs text-muted-foreground" data-testid={`${testIdPrefix}-remaining`}>
            {remaining} suggestion{remaining === 1 ? '' : 's'} left today
          </span>
        )}
        {gated && (
          <span className="text-xs text-muted-foreground italic">{tooltip}</span>
        )}
      </div>

      {error && (
        <div
          className="text-sm text-amber-700 bg-amber-50 border border-amber-200 rounded-sm px-3 py-2"
          data-testid={`${testIdPrefix}-suggest-error`}
        >
          {error}
        </div>
      )}

      {/* Value proposition — drafted directly into a preview area */}
      {section === 'value_proposition' && draft && (
        <div
          className="border border-[#B89B5E]/40 rounded-sm bg-[#FAF4E6]/50 p-3 space-y-2"
          data-testid="value-prop-suggest-draft"
        >
          <p className="text-sm text-[#1A1A1A] leading-relaxed">{draft}</p>
          <p className="text-xs text-muted-foreground italic">
            AI draft — edit to make it yours
          </p>
          <div className="flex gap-2">
            <Button
              type="button"
              size="sm"
              onClick={acceptDraft}
              className="bg-[#1A1A1A] hover:bg-[#2A2A2A]"
              data-testid="value-prop-suggest-accept"
            >
              <Check className="h-3.5 w-3.5 mr-1.5" />
              Use this draft
            </Button>
            <Button
              type="button"
              size="sm"
              variant="outline"
              onClick={runSuggest}
              disabled={loading || gated}
              data-testid="value-prop-suggest-regenerate"
            >
              <RefreshCw className="h-3.5 w-3.5 mr-1.5" />
              Regenerate
            </Button>
            <Button
              type="button"
              size="sm"
              variant="ghost"
              onClick={() => setDraft(null)}
              data-testid="value-prop-suggest-dismiss"
            >
              <X className="h-3.5 w-3.5" />
            </Button>
          </div>
        </div>
      )}

      {/* List sections — chips with ✓ Add / ✕ Dismiss */}
      {section !== 'value_proposition' && section !== 'buyer_roles' && chips.length > 0 && (
        <div className="space-y-2" data-testid={`${testIdPrefix}-suggest-chips`}>
          <p className="text-xs text-muted-foreground italic">
            Tap ✓ to add, ✕ to dismiss
          </p>
          {chips.map((text, i) => (
            <div
              key={i}
              className="flex items-start gap-2 p-2.5 bg-[#FAF4E6]/50 border border-[#B89B5E]/30 rounded-sm"
              data-testid={`${testIdPrefix}-suggest-chip-${i}`}
            >
              <span className="text-sm flex-1 text-[#1A1A1A]">{text}</span>
              <button
                type="button"
                onClick={() => castVote(text, 'up')}
                className={`transition-colors ${
                  votes[text] === 'up'
                    ? 'text-[#B89B5E]'
                    : 'text-muted-foreground/40 hover:text-[#B89B5E]'
                }`}
                data-testid={`${testIdPrefix}-suggest-chip-${i}-vote-up`}
                aria-label="Helpful"
              >
                <ThumbsUp className="h-3.5 w-3.5" />
              </button>
              <button
                type="button"
                onClick={() => castVote(text, 'down')}
                className={`transition-colors ${
                  votes[text] === 'down'
                    ? 'text-[#B89B5E]'
                    : 'text-muted-foreground/40 hover:text-[#B89B5E]'
                }`}
                data-testid={`${testIdPrefix}-suggest-chip-${i}-vote-down`}
                aria-label="Not helpful"
              >
                <ThumbsDown className="h-3.5 w-3.5" />
              </button>
              <button
                type="button"
                onClick={() => acceptChipAt(i)}
                className="text-[#1A1A1A] hover:text-[#B89B5E]"
                data-testid={`${testIdPrefix}-suggest-chip-${i}-accept`}
                title="Add to list"
              >
                <Check className="h-4 w-4" />
              </button>
              <button
                type="button"
                onClick={() => dismissChipAt(i)}
                className="text-muted-foreground hover:text-red-600"
                data-testid={`${testIdPrefix}-suggest-chip-${i}-dismiss`}
                title="Dismiss"
              >
                <X className="h-4 w-4" />
              </button>
            </div>
          ))}
        </div>
      )}

      {/* Buyer-role cards */}
      {section === 'buyer_roles' && roles.length > 0 && (
        <div className="space-y-3" data-testid="buyer-roles-suggest-cards">
          <p className="text-xs text-muted-foreground italic">
            Tap ✓ to add each role, ✕ to dismiss
          </p>
          {roles.map((role, i) => (
            <div
              key={i}
              className="p-3 bg-[#FAF4E6]/50 border border-[#B89B5E]/30 rounded-sm space-y-2"
              data-testid={`buyer-roles-suggest-card-${i}`}
            >
              <div className="flex items-start justify-between gap-2">
                <div className="font-medium text-[#1A1A1A]">{role.title}</div>
                <div className="flex gap-1">
                  <button
                    type="button"
                    onClick={() => castVote(role.title, 'up')}
                    className={`transition-colors ${
                      votes[role.title] === 'up'
                        ? 'text-[#B89B5E]'
                        : 'text-muted-foreground/40 hover:text-[#B89B5E]'
                    }`}
                    data-testid={`buyer-roles-suggest-card-${i}-vote-up`}
                    aria-label="Helpful"
                  >
                    <ThumbsUp className="h-3.5 w-3.5" />
                  </button>
                  <button
                    type="button"
                    onClick={() => castVote(role.title, 'down')}
                    className={`transition-colors ${
                      votes[role.title] === 'down'
                        ? 'text-[#B89B5E]'
                        : 'text-muted-foreground/40 hover:text-[#B89B5E]'
                    }`}
                    data-testid={`buyer-roles-suggest-card-${i}-vote-down`}
                    aria-label="Not helpful"
                  >
                    <ThumbsDown className="h-3.5 w-3.5" />
                  </button>
                  <button
                    type="button"
                    onClick={() => acceptRoleAt(i)}
                    className="text-[#1A1A1A] hover:text-[#B89B5E]"
                    data-testid={`buyer-roles-suggest-card-${i}-accept`}
                    title="Add this role"
                  >
                    <Check className="h-4 w-4" />
                  </button>
                  <button
                    type="button"
                    onClick={() => dismissRoleAt(i)}
                    className="text-muted-foreground hover:text-red-600"
                    data-testid={`buyer-roles-suggest-card-${i}-dismiss`}
                    title="Dismiss"
                  >
                    <X className="h-4 w-4" />
                  </button>
                </div>
              </div>
              {role.priorities?.length > 0 && (
                <div>
                  <div className="text-xs text-muted-foreground uppercase tracking-wider mb-1">
                    Priorities
                  </div>
                  <div className="flex flex-wrap gap-1">
                    {role.priorities.map((p, pi) => (
                      <span
                        key={pi}
                        className="text-xs bg-white border rounded-sm px-2 py-0.5"
                      >
                        {p}
                      </span>
                    ))}
                  </div>
                </div>
              )}
              {role.pain_points?.length > 0 && (
                <div>
                  <div className="text-xs text-muted-foreground uppercase tracking-wider mb-1">
                    Pain points
                  </div>
                  <div className="flex flex-wrap gap-1">
                    {role.pain_points.map((p, pi) => (
                      <span
                        key={pi}
                        className="text-xs bg-white border rounded-sm px-2 py-0.5 text-red-600"
                      >
                        {p}
                      </span>
                    ))}
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
